import { useCallback, useEffect, useRef, useState } from 'react';
import {
  checkCustomDomain,
  fetchMyCustomDomain,
  refreshAllCustomDomains,
  resolveDomainState,
} from '@/lib/customDomain';

/**
 * useDomainMonitor — vérification PÉRIODIQUE et AUTOMATIQUE des domaines.
 *
 * Le backend Readdy n'expose pas de cron serveur : la vérification tourne donc
 * automatiquement côté application (throttlée par appareil via localStorage) :
 *   - mode « merchant » : re-vérifie LE domaine du marchand toutes les 30 min ;
 *   - mode « admin »    : re-vérifie TOUS les domaines toutes les 60 min.
 *
 * Elle signale les domaines expirés / en erreur (SSL non validé, désactivé…).
 * Une action manuelle `runNow` reste disponible.
 */

const MERCHANT_INTERVAL_MS = 30 * 60 * 1000;
const ADMIN_INTERVAL_MS = 60 * 60 * 1000;
const TICK_MS = 5 * 60 * 1000;

export interface DomainAlert {
  tone: 'warning' | 'danger';
  message: string;
}

interface Params {
  userId?: number;
  userEmail?: string;
  mode: 'merchant' | 'admin';
}

export function useDomainMonitor({ userId, userEmail, mode }: Params) {
  const [checking, setChecking] = useState(false);
  const [lastRun, setLastRun] = useState<number | null>(null);
  const [alert, setAlert] = useState<DomainAlert | null>(null);
  const [expiredCount, setExpiredCount] = useState(0);
  const running = useRef(false);

  const interval = mode === 'admin' ? ADMIN_INTERVAL_MS : MERCHANT_INTERVAL_MS;
  const storageKey = userId ? `zifek-domain-monitor:${mode}:${userId}` : '';

  const runNow = useCallback(async () => {
    if (!userId || !userEmail || running.current) return;
    running.current = true;
    setChecking(true);
    try {
      if (mode === 'admin') {
        const res = await refreshAllCustomDomains({ userId, userEmail });
        if (!res.error) {
          const expired = res.expired || 0;
          setExpiredCount(expired);
          if (expired > 0) {
            setAlert({
              tone: 'danger',
              message: `${expired} domaine${expired > 1 ? 's' : ''} nécessite${expired > 1 ? 'nt' : ''} une attention (SSL ou domaine désactivé).`,
            });
          } else {
            setAlert(null);
          }
        }
      } else {
        const row = await fetchMyCustomDomain(userId);
        if (row?.domaine && !row.is_active) {
          const res = await checkCustomDomain({ userId, userEmail });
          if (res.error) {
            setAlert({ tone: 'danger', message: `Domaine ${row.domaine} : ${res.error}` });
          } else {
            const info = resolveDomainState(
              res.hostname_status ?? row.hostname_status,
              res.ssl_status ?? row.ssl_status,
              !!res.is_active,
            );
            if (info.state === 'error') {
              setAlert({
                tone: 'danger',
                message: `Domaine ${row.domaine} : ${row.error_message || 'erreur de configuration SSL.'}`,
              });
            } else if (info.state === 'ssl_pending') {
              setAlert({ tone: 'warning', message: `Le certificat SSL de ${row.domaine} est en cours de validation.` });
            } else if (info.state === 'pending_dns') {
              setAlert({ tone: 'warning', message: `Domaine ${row.domaine} : en attente de la configuration DNS.` });
            } else {
              setAlert(null);
            }
          }
        } else {
          setAlert(null);
        }
      }

      const now = Date.now();
      setLastRun(now);
      if (storageKey) {
        try { localStorage.setItem(storageKey, String(now)); } catch { /* stockage indisponible */ }
      }
    } catch {
      /* réseau : on retentera au prochain tick */
    } finally {
      running.current = false;
      setChecking(false);
    }
  }, [userId, userEmail, mode, storageKey]);

  const maybeRun = useCallback(() => {
    if (!userId || !userEmail) return;
    let last = 0;
    if (storageKey) {
      try { last = Number(localStorage.getItem(storageKey) || 0) || 0; } catch { last = 0; }
    }
    if (Date.now() - last < interval) return;
    runNow();
  }, [userId, userEmail, storageKey, interval, runNow]);

  useEffect(() => {
    if (!userId || !userEmail) return;
    if (storageKey) {
      try {
        const raw = localStorage.getItem(storageKey);
        if (raw) setLastRun(Number(raw) || null);
      } catch { /* ignore */ }
    }
    maybeRun();
    const timer = setInterval(maybeRun, TICK_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') maybeRun();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [userId, userEmail, storageKey, maybeRun]);

  return { checking, lastRun, alert, expiredCount, runNow };
}

export default useDomainMonitor;